import Vue from 'vue'

import * as types from '../mutation-types'

import users from '@/api/users'
import messages from '@/api/messages'

import socket from '@/libs/socket'

const draftID = 'draft'
const publicKey = 'public'
const privateKey = 'private'

const state = {
  public: { conversations: {}, total: 0, hasMore: false, sorted: [] },
  private: { conversations: {}, total: 0, hasMore: false, sorted: [] },
  draft: null,
  unreadCount: 0,
  likes: {},
  unreadConversations: {}
}

const getters = {
  draftConversation: (state) => {
    return state.draft
  },
  conversations: (state) => {
    return state.private.sorted
  },
  unreadConversationsCount: (state) => {
    return state.unreadCount
  },
  publicConversations: (state) => {
    return state.public.sorted
  },
  getConversation: (state) => (id) => {
    if (id === draftID) {
      return state.draft
    }

    return getConversationByID(state, id) || null
  },
  likedMessage: (state) => (id, username) => {
    return state.likes[id] ? state.likes[id][username] : false
  },
  isConversationPublic: (state) => (id) => {
    return isPublic(state, id)
  }
}

const getMessageIndex = function (id, commentId) {
  const key = conversationKey(id)
  let index = 0
  if (state.public.conversations[key]) {
    state.public.conversations[key].messages.forEach((message, offset) => {
      if (message.id === commentId) {
        index = offset
      }
    })
  } else {
    state.private.conversations[key].messages.forEach((message, offset) => {
      if (message.id === commentId) {
        index = offset
      }
    })
  }

  return index
}

const isPublic = function (state, id) {
  return !!state.public.conversations[conversationKey(id)]
}

const getConversationByID = function (state, id) {
  const key = conversationKey(id)
  return state.private.conversations[key] ? state.private.conversations[key] : state.public.conversations[key]
}

/* eslint-disable prefer-promise-reject-errors */
const actions = {
  createDraftConversation ({ commit }, users) {
    const conversation = { id: draftID, draft: true, messages: [], users }
    commit(types.SET_DRAFT_CONVERSATION, conversation)
    return conversation
  },
  cancelDraftConversation ({ commit }) {
    commit(types.SET_DRAFT_CONVERSATION, null)
  },
  createConversation ({ commit }, users) {
    return new Promise((resolve, reject) => {
      messages.createConversation(users).then((response) => {
        const conversation = response.data.result.conversation
        conversation.messages = response.data.result.messages
        commit(types.ADD_CONVERSATION, conversation)
        commit(types.SET_DRAFT_CONVERSATION, null)
        commit(types.SORT_CONVERSATIONS, conversation.public ? publicKey : privateKey)
        resolve(conversation)
      }, (error) => {
        reject(error)
      })
    })
  },
  getConversation ({ commit, state }, id) {
    return new Promise((resolve, reject) => {
      messages.getConversation(id).then((response) => {
        const conversation = response.data.result.conversation
        const unreadCount = response.data.result.totalUnread
        commit(types.ADD_CONVERSATION, conversation)
        commit(types.SET_CONVERSATION_UNREAD, { id: conversation.id, unread: true })
        commit(types.SET_UNREAD_CONVERSATIONS_COUNT, unreadCount)
        commit(types.SORT_CONVERSATIONS, conversation.public ? publicKey : privateKey)
        resolve(conversation)
      }, (error) => {
        reject(error)
      })
    })
  },
  getConversationToMessage ({ commit, state, dispatch }, { conversationID, messageID }) {
    return new Promise((resolve, reject) => {
      messages.getConversationToMessage(conversationID, messageID).then((response) => {
        const conversation = response.data.result.conversation
        const unreadCount = response.data.result.totalUnread
        commit(types.ADD_CONVERSATION, conversation)
        commit(types.SET_CONVERSATION_UNREAD, { id: conversation.id, unread: true })
        commit(types.SET_UNREAD_CONVERSATIONS_COUNT, unreadCount)
        commit(types.SORT_CONVERSATIONS, conversation.public ? publicKey : privateKey)
        resolve(conversation)
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectConversation ({ commit, state }, projectID) {
    return new Promise((resolve, reject) => {
      messages.getProjectConversation(projectID).then((response) => {
        const conversation = response.data.result.conversation
        if (conversation) {
          commit(types.ADD_CONVERSATION, conversation)
          commit(types.SORT_CONVERSATIONS, conversation.public ? publicKey : privateKey)
        }
        resolve(conversation)
      }, (error) => {
        reject(error)
      })
    })
  },
  getChatConversation ({ commit, state }, { projectID, chatType }) {
    return new Promise((resolve, reject) => {
      messages.getChatConversation(projectID, chatType).then((response) => {
        const conversation = response.data.result.conversation
        if (conversation) {
          commit(types.ADD_CONVERSATION, conversation)
          commit(types.SORT_CONVERSATIONS, conversation.public ? publicKey : privateKey)
        }
        resolve(conversation)
      }, (error) => {
        reject(error)
      })
    })
  },
  createChatConversation ({ commit, state }, { projectID, chatType }) {
    return new Promise((resolve, reject) => {
      messages.createChatConversation(projectID, chatType).then((response) => {
        const conversation = response.data.result.conversation
        commit(types.ADD_CONVERSATION, conversation)
        commit(types.SORT_CONVERSATIONS, conversation.public ? publicKey : privateKey)
        resolve(conversation)
      }, (error) => {
        reject(error)
      })
    })
  },
  deleteChatConversation ({ commit }, { conversationID }) {
    return new Promise((resolve, reject) => {
      messages.deleteChatConversation(conversationID).then(() => {
        commit(types.REMOVE_CONVERSATION, conversationID)
        resolve()
      }).catch((error) => {
        reject(error)
      })
    })
  },
  createProjectConversation ({ commit, state }, projectID) {
    return new Promise((resolve, reject) => {
      messages.createProjectConversation(projectID).then((response) => {
        const conversation = response.data.result.conversation
        commit(types.ADD_CONVERSATION, conversation)
        commit(types.SORT_CONVERSATIONS, conversation.public ? publicKey : privateKey)
        resolve(conversation)
      }, (error) => {
        reject(error)
      })
    })
  },
  getConversations ({ commit, state }, { username, query, limit, offset }) {
    return new Promise((resolve, reject) => {
      const numInState = Object.keys(state.private.conversations).length
      if (numInState >= limit + offset || (numInState > 0 && offset === 0)) {
        resolve(state.private)
        return
      }
      messages.getConversations(username, query, limit, offset).then((response) => {
        const conversations = response.data.result.conversations
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore

        conversations.forEach((conversation) => {
          commit(types.ADD_CONVERSATION, conversation)
        })

        commit(types.SORT_CONVERSATIONS, privateKey)

        commit(types.SET_CONVERSATIONS_METADATA, { total, hasMore })

        resolve({ conversations, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getPublicConversations ({ commit, state }, { query, limit, offset }) {
    return new Promise((resolve, reject) => {
      const numInState = Object.keys(state.public.conversations).length
      if (numInState >= limit + offset || (numInState > 0 && offset === 0)) {
        resolve(state.public)
        return
      }

      messages.getPublicConversations(query, limit, offset).then((response) => {
        const conversations = response.data.result.conversations
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore

        conversations.forEach((conversation) => {
          commit(types.ADD_PUBLIC_CONVERSATION, conversation)
        })

        commit(types.SORT_CONVERSATIONS, publicKey)

        commit(types.SET_PUBLIC_CONVERSATIONS_METADATA, { total, hasMore })

        resolve({ conversations, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getPublicConversationByTopic ({ commit, state }, topic) {
    return new Promise((resolve, reject) => {
      let found = null
      Object.keys(state.public.conversations).forEach((key) => {
        if (state.public.conversations[key].topic === topic) {
          found = key
        }
      })

      if (found) {
        return resolve(state.public.conversations[found])
      }

      messages.getPublicConversationByTopic(topic).then((response) => {
        const conversation = response.data.result.conversation
        commit(types.ADD_PUBLIC_CONVERSATION, conversation)
        resolve(conversation)
      }, (error) => {
        reject(error)
      })
    })
  },
  getGroupConversationByTopic ({ commit, state }, topic) {
    return new Promise((resolve, reject) => {
      let found = null
      Object.keys(state.private.conversations).forEach((key) => {
        if (state.private.conversations[key].topic === topic) {
          found = key
        }
      })

      if (found) {
        return resolve(state.private.conversations[found])
      }

      messages.getGroupConversationByTopic(topic).then((response) => {
        const conversation = response.data.result.conversation
        commit(types.ADD_CONVERSATION, conversation)
        resolve(conversation)
      }, (error) => {
        reject(error)
      })
    })
  },
  getMessages ({ commit, state }, { id, limit, before, since }) {
    let add = types.ADD_MESSAGE_BEFORE
    if (since > 0) {
      add = types.ADD_MESSAGE_AFTER
    }
    return new Promise((resolve, reject) => {
      // @todo add logic to re-use messages in store
      messages.getMessagesInConversation(id, limit, before, since).then((response) => {
        const messages = response.data.result.messages
        const hasMore = response.data.result.hasMore
        messages.forEach((message) => {
          commit(add, { id, message })
        })

        if (before > 0) {
          commit(types.SET_CONVERATION_HAS_MORE_MESSAGES, { id, hasMore })
        }
        commit(types.SORT_CONVERSATIONS, privateKey)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getPublicMessages ({ commit, state }, { id, limit, before, since }) {
    let add = types.ADD_MESSAGE_BEFORE
    if (since > 0) {
      add = types.ADD_MESSAGE_AFTER
    }
    return new Promise((resolve, reject) => {
      // @todo add logic to re-use messages in store
      messages.getMessagesInPublicConversation(id, limit, before, since).then((response) => {
        const messages = response.data.result.messages
        const hasMore = response.data.result.hasMore
        messages.forEach((message) => {
          commit(add, { id, message })
        })

        if (before > 0) {
          commit(types.SET_CONVERATION_HAS_MORE_MESSAGES, { id, hasMore })
        }
        commit(types.SORT_CONVERSATIONS, publicKey)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  sendMessage ({ commit, state }, { id, message, chatType }) {
    return new Promise((resolve, reject) => {
      messages.sendMessage(id, message, chatType).then((response) => {
        const message = response.data.result.message
        commit(types.ADD_MESSAGE_AFTER, { id, message })
        commit(types.SORT_CONVERSATIONS, isPublic(state, id) ? publicKey : privateKey)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getUnreadCount ({ commit }, username) {
    return new Promise((resolve, reject) => {
      messages.getUnreadCount(username).then((response) => {
        const count = response.data.result.total
        commit(types.SET_UNREAD_CONVERSATIONS_COUNT, count)
        resolve(count)
      }, (error) => {
        reject(error)
      })
    })
  },
  markConversationRead ({ commit }, conversation) {
    return new Promise((resolve, reject) => {
      messages.markConversationRead(conversation.id).then((response) => {
        commit(types.SET_CONVERSATION_UNREAD, { id: conversation.id, unread: false })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getUsersInConversation ({ commit }, conversationID) {
    return new Promise((resolve, reject) => {
      messages.getUsersInConversation(conversationID, 0, 0).then((response) => {
        const users = response.data.result.users
        resolve(users)
      }, (error) => {
        reject(error)
      })
    })
  },
  removeUserFromConversation ({ commit }, { conversationID, userName }) {
    return new Promise((resolve, reject) => {
      messages.removeUserFromConversation(conversationID, userName).then(() => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  addUserToConversation ({ commit }, { conversationID, user }) {
    return new Promise((resolve, reject) => {
      messages.addUserToConversation(conversationID, { userName: user.name, isConversationAdmin: user.isConversationAdmin, conversationRole: user.conversationRole }).then(() => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  updateUserConversationRole ({ commit }, { conversationID, user }) {
    return new Promise((resolve, reject) => {
      messages.updateUserConversationRole(conversationID, user.id, user.isConversationAdmin).then((response) => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  socketMessage ({ dispatch, state, commit }, message) {
    const payload = message.payload || {}
    if (payload.type === 'conversation' && payload.action === 'add') {
      const conversation = getConversationByID(state, payload.id)
      if (conversation) {
        const since = conversation.messages.length === 0 ? 0 : conversation.messages[conversation.messages.length - 1].id
        dispatch('getMessages', { id: payload.id, since })

        if (socket.isSubscribed(payload.type, payload.id, 'message')) {
          dispatch('markConversationRead', conversation)
        } else {
          commit(types.SET_CONVERSATION_UNREAD, { id: payload.id, unread: true })
        }
      } else if (Object.keys(state.private.conversations).length > 0) {
        dispatch('getConversation', payload.id)
      } else {
        commit(types.INCR_UNREAD_CONVERSATIONS_COUNT, payload.id)
      }
    }
  },
  cacheLikedMessages ({ commit }, username) {
    if (state.likes.cached && state.likes.cached[username]) {
      return
    }

    users.getLikedMessageIDs(username).then((response) => {
      commit(types.LIKED_MESSAGES_CACHED, { username })
      response.data.result.messages.forEach((id) => {
        commit(types.LIKED_MESSAGE, { id, username, liked: true })
      })
    })
  },
  likeMessage ({ commit }, { type, id, username, conversationId }) {
    return new Promise((resolve, reject) => {
      messages.like(id).then((response) => {
        const index = getMessageIndex(conversationId, id)
        commit(types.UPDATE_MESSAGE_LIKECOUNT, { id: conversationId, index, countChange: 1 })
        commit(types.LIKED_MESSAGE, { id, username, liked: true, index })
        resolve()
      })
    })
  },
  messageLikedBy ({ commit }, { id, limit, offset }) {
    return new Promise((resolve, reject) => {
      messages.getLikes(id, limit, offset).then((response) => {
        const users = response.data.result.users
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        resolve({ users, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  updateMessage ({ commit }, { conversationID, messageID, message }) {
    return new Promise((resolve, reject) => {
      messages.updateMessage(messageID, message).then((response) => {
        const responseMessage = response.data.result.message
        commit(types.UPDATE_MESSAGE, { conversationID, message: responseMessage })
        resolve(responseMessage)
      }, (error) => {
        reject(error)
      })
    })
  },
  unlikeMessage ({ commit }, { type, id, username, conversationId }) {
    return new Promise((resolve, reject) => {
      messages.unlike(id).then((response) => {
        const index = getMessageIndex(conversationId, id)
        commit(types.UPDATE_MESSAGE_LIKECOUNT, { id: conversationId, index, countChange: -1 })
        commit(types.LIKED_MESSAGE, { id, username, liked: false })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  likedMessage ({ commit, state }, { id, username }) {
    return new Promise((resolve, reject) => {
      if (state.likes[id] && state.likes[id][username] !== undefined) {
        return resolve(state.likes[id][username])
      }

      if (state.likes.cached && state.likes.cached[username]) {
        return resolve(false)
      }

      messages.hasLiked(id, username).then((response) => {
        const liked = response.data.result.liked
        commit(types.LIKED_MESSAGE, { id, username, liked })
        resolve(liked)
      }, (error) => {
        reject(error)
      })
    })
  },
  deleteMessage ({ commit, state }, { conversationID, messageID, showPermission }) {
    return new Promise((resolve, reject) => {
      messages.deleteMessage(messageID, showPermission).then(() => {
        commit(types.DELETE_MESSAGE, { conversationID, messageID })
        resolve()
      }, () => {
        reject()
      })
    })
  },
  muteConversationForUser ({ commit }, { conversationID, notification }) {
    return new Promise((resolve, reject) => {
      messages.updateConversationNotificationSettings(conversationID, notification).then(() => {
        resolve()
      }, () => {
        reject()
      })
    })
  },
  createGroupConversation ({ commit }, { data }) {
    return new Promise((resolve, reject) => {
      messages.createGroupConversation(data).then((response) => {
        commit(types.ADD_CONVERSATION, response.data.result.conversation)
        resolve(response.data.result.conversation)
      }, () => {
        reject()
      })
    })
  },
  updateGroupConversation ({ commit }, { conversationID, data }) {
    return new Promise((resolve, reject) => {
      messages.updateGroupConversation(conversationID, data).then((response) => {
        commit(types.ADD_CONVERSATION, response.data.result.conversation)
        resolve()
      }, () => {
        reject()
      })
    })
  },
  getUserConversationRole ({ commit }, conversationID) {
    return new Promise((resolve, reject) => {
      messages.getUserConversationRole(conversationID).then((response) => {
        resolve(response.data.result.isConversationAdmin)
      }, () => {
        reject()
      })
    })
  }
}

const mutations = {
  [types.SET_CONVERSATIONS_METADATA] (state, params) {
    state.private.total = params.total
    state.private.hasMore = params.hasMore
  },
  [types.ADD_CONVERSATION] (state, conversation) {
    const key = conversationKey(conversation.id)
    conversation.hasMore = true
    conversation.messages = conversation.messages || []
    Vue.set(state.private.conversations, key, conversation)
  },
  [types.ADD_MESSAGE_BEFORE] (state, params) {
    const key = conversationKey(params.id)
    if (state.private.conversations[key]) {
      state.private.conversations[key].messages.unshift(params.message)
    } else if (state.public.conversations[key]) {
      state.public.conversations[key].messages.unshift(params.message)
    }
  },
  [types.ADD_MESSAGE_AFTER] (state, params) {
    const key = conversationKey(params.id)
    // Ignore public conversations
    if (state.private.conversations[key]) {
      state.private.conversations[key].messages.push(params.message)
    } else if (state.public.conversations[key]) {
      state.public.conversations[key].messages.push(params.message)
    }
  },
  [types.SET_CONVERATION_HAS_MORE_MESSAGES] (state, params) {
    const key = conversationKey(params.id)
    if (state.private.conversations[key]) {
      Vue.set(state.private.conversations[key], 'hasMoreMessages', params.hasMore)
    } else if (state.public.conversations[key]) {
      Vue.set(state.public.conversations[key], 'hasMoreMessages', params.hasMore)
    }
  },
  [types.SET_DRAFT_CONVERSATION] (state, conversation) {
    state.draft = conversation
    if (state.private.sorted.length && state.private.sorted[0].draft) {
      state.private.sorted.shift()
    }
    if (conversation) {
      state.private.sorted.unshift(state.draft)
    }
  },
  [types.SORT_CONVERSATIONS] (state, conversationType) {
    if (!state[conversationType] || !state[conversationType].conversations) return

    const isPublic = conversationType === publicKey
    state[conversationType].sorted = []
    Object.keys(state[conversationType].conversations).forEach((key) => {
      const conversation = state[conversationType].conversations[key]

      if (conversation.messages.length === 0 && conversation.topic === '') {
        return
      }

      if (conversation.public === isPublic) {
        state[conversationType].sorted.push(conversation)
      }
    })

    state[conversationType].sorted.sort((a, b) => {
      if (a.pinned === b.pinned && a.unread === b.unread && a.messages.length > 0 && b.messages.length > 0) {
        return b.messages[b.messages.length - 1].id - a.messages[a.messages.length - 1].id
      }

      if (a.pinned) {
        return -1
      }

      if (b.pinned) {
        return 1
      }

      if (a.unread) {
        return -1
      }

      if (b.unread) {
        return 1
      }

      if (a.messages.length === 0) {
        return 1
      }

      if (b.messages.length === 0) {
        return -1
      }

      return 0
    })

    if (state.draft) {
      state[conversationType].sorted.unshift(state.draft)
    }
  },
  [types.INCR_UNREAD_CONVERSATIONS_COUNT] (state, conversationId) {
    if (!(conversationId in state.unreadConversations)) {
      state.unreadCount += 1
    }
    state.unreadConversations[conversationId] = true
  },
  [types.SET_UNREAD_CONVERSATIONS_COUNT] (state, count) {
    state.unreadCount = count
    state.unreadConversations = {}
  },
  [types.SET_CONVERSATION_UNREAD] (state, params) {
    const key = conversationKey(params.id)
    let current = ''
    if (state.private.conversations[key]) {
      current = state.private.conversations[key].unread
      state.private.conversations[key].unread = params.unread
    } else if (state.public.conversations[key]) {
      current = state.public.conversations[key].unread
      state.public.conversations[key].unread = params.unread
    }
    if (current !== params.unread) {
      state.unreadCount += params.unread ? 1 : -1
    }
    if (state.unreadCount < 0) {
      state.unreadCount = 0
    }
  },
  [types.SET_PUBLIC_CONVERSATIONS_METADATA] (state, params) {
    state.public.total = params.total
    state.public.hasMore = params.hasMore
  },
  [types.ADD_PUBLIC_CONVERSATION] (state, conversation) {
    const key = conversationKey(conversation.id)
    conversation.messages = conversation.messages || []
    Vue.set(state.public.conversations, key, conversation)
  },
  [types.RESET_ON_LOGOUT] (state) {
    state.private.conversations = {}
    state.private.sorted = []
    state.private.total = 0
    state.private.hasMore = false
    state.draft = null
    state.unreadCount = 0
    state.unreadConversations = {}
  },
  [types.LIKED_MESSAGE] (state, params) {
    if (!state.likes[params.id]) {
      Vue.set(state.likes, params.id, {})
    }

    Vue.set(state.likes[params.id], params.username, params.liked)
  },
  [types.UPDATE_MESSAGE_LIKECOUNT] (state, params) {
    if (params.index !== null) {
      const key = conversationKey(params.id)
      if (state.public.conversations[key]) {
        state.public.conversations[key].messages[params.index].metadata.likes += params.countChange
      } else {
        state.private.conversations[key].messages[params.index].metadata.likes += params.countChange
      }
    }
  },
  [types.UPDATE_MESSAGE] (state, params) {
    const key = conversationKey(params.conversationID)
    if (state.private.conversations[key]) {
      let index = null
      state.private.conversations[key].messages.forEach((message, offset) => {
        if (message.id === params.message.id) {
          index = offset
        }
      })
      if (index !== null) {
        state.private.conversations[key].messages.splice(index, 1, params.message)
      }
    }
    if (state.public.conversations[key]) {
      let index = null
      state.public.conversations[key].messages.forEach((message, offset) => {
        if (message.id === params.message.id) {
          index = offset
        }
      })
      if (index !== null) {
        state.public.conversations[key].messages.splice(index, 1, params.message)
      }
    }
  },
  [types.DELETE_MESSAGE] (state, params) {
    const key = conversationKey(params.conversationID)
    if (state.private.conversations[key]) {
      state.private.conversations[key].messages = state.private.conversations[key].messages.filter((message) => {
        return message.id !== params.messageID
      })
    }
    if (state.public.conversations[key]) {
      state.public.conversations[key].messages = state.public.conversations[key].messages.filter((message) => {
        return message.id !== params.messageID
      })
    }
  },
  [types.REMOVE_CONVERSATION] (state, conversationID) {
    // Remove from private conversations
    const privateKey = conversationKey(conversationID)
    Vue.delete(state.private.conversations, privateKey)

    // Remove from public conversations
    const publicKey = conversationKey(conversationID)
    Vue.delete(state.public.conversations, publicKey)
  },
  [types.LIKED_MESSAGES_CACHED] (state, params) {
    if (!state.likes.cached) {
      state.likes.cached = {}
    }

    state.likes.cached[params.username] = true
  }
}

const conversationKey = function (id) {
  return `c-${id}`
}

export default {
  state,
  getters,
  actions,
  mutations
}
